import React, {ReactNode} from 'react';
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  HomeIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  WrenchIcon
} from "@heroicons/react/24/outline";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/Sheet"
import {Link, useLocation} from "react-router-dom";
import { useTour } from '@reactour/tour'
import {useSession} from "@/hooks/useSession";
import {authenticatedRequestBody} from "@/api/services/auth";
import {endpoints} from "@/api/endpoints";


const HomeElement = () => {
  return (
    <Link to="/home">
      <div className={"flex flex-row items-center gap-2"}>
        <HomeIcon className={"h-4 w-4"} />
        <span>Home</span>
      </div>
    </Link>
  )
}

const AppElement = () => {
  return (
    <Link to="/app">
      <div className={"flex flex-row items-center gap-2"}>
        <HomeIcon className={"h-4 w-4"} />
        App
      </div>
    </Link>
  )
}

const AccountElement = () => {
  return (
    <Link to="/account">
      <div className={"flex flex-row items-center gap-2"}>
        <UserCircleIcon className={"h-4 w-4"} />
        <span>Account</span>
      </div>
    </Link>
  )
}

const LogOutElement = () => {
  return (
    <Link to="/logout">
      <div className={"flex flex-row items-center gap-2"}>
        <ArrowLeftStartOnRectangleIcon className={"h-4 w-4"} />
        <span>Log Out</span>
      </div>
    </Link>
  )
}

const LogInElement = () => {
  return (
    <Link to="/login">
      <div className={"flex flex-row items-center gap-2"}>
        <ArrowLeftEndOnRectangleIcon className={"h-4 w-4"} />
        <span>Log In</span>
      </div>
    </Link>
  )

}

const HelpElement = () => {
  const { setIsOpen } = useTour();

  return (
    <div className={"flex flex-row items-center gap-2 cursor-pointer"} onClick={() => setIsOpen(true)}>
      <QuestionMarkCircleIcon className={"h-4 w-4"} />
      <span>Help</span>
    </div>
  )
}

const MoreToolsElement = () => {
  return (
    <Link to="/more-tools">
      <div className={"flex flex-row items-center gap-2"}>
        <WrenchIcon className={"h-4 w-4"} />
        <span>More Tools</span>
      </div>
    </Link>
  )
}

const ESTIMATE_URL = "https://estimate.nestrr.net";

const EstimateElement = ({isLoggedIn}: {isLoggedIn: boolean}) => {
  const [navigating, setNavigating] = React.useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    if (!isLoggedIn) return; // fall through to plain link

    e.preventDefault();
    setNavigating(true);

    try {
      const body = await authenticatedRequestBody(null);
      const resp = await fetch(endpoints.handoff, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body,
      });

      if (!resp.ok) throw new Error("Handoff request failed");

      const {code} = await resp.json();
      const url = new URL(ESTIMATE_URL);
      url.searchParams.set("code", code);
      window.location.assign(url.toString());
    } catch {
      // Fallback: navigate without handoff
      window.location.assign(ESTIMATE_URL);
    } finally {
      setNavigating(false);
    }
  };

  return (
    <a href={ESTIMATE_URL} onClick={handleClick}>
      <div className={"flex flex-row items-center gap-2"}>
        <WrenchIcon className={"h-4 w-4"} />
        <span>{navigating ? "Redirecting..." : "Estimate"}</span>
      </div>
    </a>
  );
}

interface HeaderContainerProps {
  links: ReactNode[];
}

/**
 * Container for the header. Wraps functionality for rendering the links as a sidebar on mobile.
 * @param links - `ReactNode` elements to render as links or buttons in the header.
 */
const HeaderContainer = ({links}: HeaderContainerProps) => {
  // state for the sidebar. Used for mobile only.
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <header className="flex items-center p-4 bg-primary text-white shadow">
      <div className={"flex-grow"}>
        <div className={"w-fit"}>
          <Link to={"/home"}>
            <h1 className={"flex flex-row items-center gap-2"}>
              <img src={"/icon_small.png"} alt={"Nestrr Logo"} className={"h-8 w-8 -mt-1 -mb-1"} />
              Nestrr
            </h1>
          </Link>
        </div>
      </div>
      <nav className="gap-4 hidden md:flex">
        <ul className={"flex flex-row gap-4"}>
          {links.map((link, index) => (
            <li key={index}>
              {link}
            </li>
          ))}
        </ul>
      </nav>
      <Sheet open={sidebarOpen} onOpenChange={() => setSidebarOpen(!sidebarOpen)}>
        <SheetTrigger onClick={() => setSidebarOpen(true)}>
          <Bars3Icon className={"h-6 w-6 md:hidden"} />
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <nav className={"mt-6"}>
            <ul className={"flex flex-col gap-4"}>
              {links.map((link, index) => (
                <li key={index} onClick={() => setSidebarOpen(false)}>
                  {link}
                </li>
              ))}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}


/**
 * Dynamic header which changes it's values depending on where the user is, and if the user is logged in.
 * @constructor
 */
export const Header = () => {
  const {session} = useSession();

  const location = useLocation();

  const onAppScreen = location.pathname === "/app";
  const isLoggedIn = !!session;

  const linkElements = {
    home: {element: <HomeElement />, condition: !isLoggedIn},
    app: {element: <AppElement />, condition: !onAppScreen && isLoggedIn},
    estimate: {element: <EstimateElement isLoggedIn={isLoggedIn} />, condition: true},
    moreTools: {element: <MoreToolsElement />, condition: true},
    account: {element: <AccountElement />, condition: isLoggedIn},
    logout: {element: <LogOutElement />, condition: isLoggedIn},
    login: {element: <LogInElement />, condition: !isLoggedIn && !onAppScreen},
    help: {element: <HelpElement />, condition: onAppScreen}
  };

  const links = Object.values(linkElements)
    .filter(({condition}) => condition)
    .map(({element}) => element);

  return <HeaderContainer links={links} />;
};


/**
 * Static header which only contains the Home and Log In links.
 *
 * The logo is also a link to the home page.
 */
export const StaticHeader = () => {
  const links = [<HomeElement />, <LogInElement />]

  return <HeaderContainer links={links} />
}